import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import Styles from './BrandKitColors.module.scss'
import { BrandKitPreviewCard } from './BrandKitPreviewCard/BrandKitPreviewCard'
import { useState, useEffect, useMemo } from 'react'
import { BrandKitColorSelector } from 'components/BrandKitForm/BrandKitColorSelector/BrandKitColorSelector'
import { BrandKitFonts } from './BrandKitFonts'
import { FONT_PAIRINGS, FontPairing } from 'constants/fontPairings'
import { getBrandKitFontPairing } from 'hooks/queries/brandKits/getBrandKitFontPairing'
import { sample } from 'lodash'
import { useHandleNext } from './useHandleNext'
import { useOptimizedPalettes } from './utils'
import { BrandKitColorsAndFontsContentProps, BrandKitColorsAndFontsProps } from './Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import { AddFont1, ColorSwatch2 } from 'blaze-ui/icons'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ColorsDrawer } from './ColorsDrawer'
import { FontsDrawer } from './FontsDrawer'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { ConfirmBackModal } from './ConfirmBackModal/ConfirmBackModal'
import { useDeleteBrandKit } from 'hooks/queries/brandKits/useDeleteBrandKit'

export const BrandKitColorsAndFontsContent = observer(
  ({
    fontPairingState,
    colorPaletteState,
    setColorPaletteState,
    brandKit,
    selectedFontPairing,
    setSelectedFontPairing,
  }: BrandKitColorsAndFontsContentProps) => {
    const processedPalettes = useOptimizedPalettes(colorPaletteState)
    const [colors, setColors] = useState<string[]>(processedPalettes[colorPaletteState.selectedPaletteIndex || 0] || [])
    const { drawersStore } = useRootStore()

    useEffect(() => {
      setColors(processedPalettes[colorPaletteState.selectedPaletteIndex || 0] || [])
    }, [colorPaletteState.selectedPaletteIndex, processedPalettes])

    const handlePaletteSelect = (palette: string[], index: number) => {
      setColors(palette)

      setColorPaletteState((prev) => ({
        ...prev,
        selectedPaletteIndex: index,
      }))
    }

    return (
      <div className={Styles.brandKitColors}>
        <BrandKitPreviewCard colors={colors} fontPairing={selectedFontPairing} brandKit={brandKit} />
        <ResponsiveDesign.Desktop>
          <div className={Styles.colorAndFontSelector}>
            <BrandKitColorSelector
              palettes={processedPalettes}
              selectedPaletteIndex={colorPaletteState.selectedPaletteIndex}
              websitePaletteIndex={0}
              onPaletteSelect={handlePaletteSelect}
            />
            <BrandKitFonts
              fontPairingState={fontPairingState}
              fontPairing={selectedFontPairing}
              onFontPairingChange={setSelectedFontPairing}
            />
          </div>
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <div className={Styles.colorAndFontSelectorTriggers}>
            <Button
              variant="tertiary"
              size="xl"
              fullWidth
              onPress={() =>
                drawersStore.openDrawer(ColorsDrawer, {
                  palettes: processedPalettes,
                  selectedPaletteIndex: colorPaletteState.selectedPaletteIndex || 0,
                  websitePaletteIndex: 0,
                  onPaletteSelect: handlePaletteSelect,
                  onConfirm: drawersStore.closeDrawer,
                })
              }
              frontIcon={ColorSwatch2}
            >
              Pick Colors
            </Button>
            <Button
              variant="tertiary"
              size="xl"
              fullWidth
              onPress={() =>
                drawersStore.openDrawer(FontsDrawer, {
                  fontPairingState,
                  selectedFontPairing,
                  setSelectedFontPairing,
                  onConfirm: drawersStore.closeDrawer,
                })
              }
              frontIcon={AddFont1}
            >
              Pick Fonts
            </Button>
          </div>
        </ResponsiveDesign.Mobile>
      </div>
    )
  },
)

export const BrandKitColorsAndFonts = ({
  onNext,
  onBack,
  onSkip,
  brandKit,
  colorPaletteState,
  setColorPaletteState,
  title,
  fontPairingState,
}: BrandKitColorsAndFontsProps) => {
  const { mutate: deleteBrandKit } = useDeleteBrandKit({ id: brandKit.id })
  const backConfirm = useConfirmModal(ConfirmBackModal)
  const processedPalettes = useOptimizedPalettes(colorPaletteState)
  const kitPairings = useMemo(
    () => (brandKit.brand_visual_kit ? getBrandKitFontPairing(brandKit.brand_visual_kit) : null) as FontPairing,
    [brandKit],
  )

  const [colors, setColors] = useState<string[]>(processedPalettes[colorPaletteState.selectedPaletteIndex || 0] || [])
  const [selectedFontPairing, setSelectedFontPairing] = useState<FontPairing>(kitPairings || sample(FONT_PAIRINGS))

  const { handleNext, isLoading } = useHandleNext({
    fontPairing: selectedFontPairing,
    colors,
    brandKit,
    onNext,
  })

  const handleBack = async () => {
    const confirm = await backConfirm()

    if (confirm) {
      deleteBrandKit()
      onBack?.()
    }
  }

  // Update colors when selected palette changes
  useEffect(() => {
    setColors(processedPalettes[colorPaletteState.selectedPaletteIndex || 0] || [])
  }, [colorPaletteState.selectedPaletteIndex, processedPalettes])

  return (
    <MultiStepStepLayout
      title={title}
      onBack={handleBack}
      onNext={handleNext}
      onSkip={onSkip}
      nextButtonText="Review Images"
      isNextButtonDisabled={isLoading}
    >
      <BrandKitColorsAndFontsContent
        fontPairingState={fontPairingState}
        colorPaletteState={colorPaletteState}
        setColorPaletteState={setColorPaletteState}
        brandKit={brandKit}
        selectedFontPairing={selectedFontPairing}
        setSelectedFontPairing={setSelectedFontPairing}
      />
    </MultiStepStepLayout>
  )
}
